import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useAuth } from './AuthContext'
import { useApiClient } from '../hooks/useApiClient'
import { NOTIFICATIONS_ENDPOINT } from '../Constant'

const NotificationContext = createContext(null)

export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const { authFetch } = useApiClient()
  const [notifications, setNotifications] = useState([])
  const intervalRef = useRef(null)

  const unreadCount = notifications.filter((n) => !n.isRead).length

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const res = await authFetch(NOTIFICATIONS_ENDPOINT)
      if (!res || !res.ok) return
      const json = await res.json()
      setNotifications(json.data || [])
    } catch {
      // silently ignore fetch errors
    }
  }, [isAuthenticated, authFetch])

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const encodedId = encodeURIComponent(notificationId)
      const res = await authFetch(`${NOTIFICATIONS_ENDPOINT}/${encodedId}/read`, { method: 'PUT' })
      if (!res || !res.ok) return
      setNotifications((prev) => prev.filter((n) => n.notificationId !== notificationId))
    } catch {
      // silently ignore
    }
  }, [authFetch])

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([])
      return
    }
    fetchNotifications()
    intervalRef.current = setInterval(fetchNotifications, 5 * 60 * 1000)
    return () => clearInterval(intervalRef.current)
  }, [isAuthenticated, fetchNotifications])

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, fetchNotifications, markAsRead }}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => useContext(NotificationContext)
